"use client";

import React from "react";
import EditSection, {
  TextEditor,
  ListItemEditor,
  ToggleEditor,
  ImageEditor,
} from "../../sections/shared/EditSection";
import { SectionContentProps } from "../../types";

export default function HokHeaderEdit({
  content,
  onContentChange,
  allSections = [],
}: SectionContentProps & { allSections?: any[] }) {
  const handleChange = (updated: Record<string, any>) => {
    onContentChange?.(updated);
  };

  const sectionOptions = [
    { value: "", label: "Select Section..." },
    { value: "custom", label: "Custom Link (Use Custom URL)" },
    ...(allSections || [])
      .filter((s: any) => s && s.id)
      .map((s: any) => ({
        value: s.id,
        label: `${s.id} (${s.type})`,
      })),
  ];

  return (
    <EditSection
      content={content}
      onContentChange={handleChange}
      contentFields={
        <>
          {/* Logo & Company */}
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Header Settings</h4>
            <TextEditor
              label="Company Name"
              value={content.companyName}
              onChange={(v) => handleChange({ ...content, companyName: v })}
              placeholder="HOK"
            />
            <ImageEditor
              label="Logo"
              value={content.logo}
              onChange={(v) => handleChange({ ...content, logo: v })}
              placeholder="/assets/img/logo.svg"
            />
            <TextEditor
              label="Search Placeholder"
              value={content.searchPlaceholder}
              onChange={(v) => handleChange({ ...content, searchPlaceholder: v })}
              placeholder="Search for courses..."
            />
            <TextEditor
              label="CTA Button Text"
              value={content.ctaText}
              onChange={(v) => handleChange({ ...content, ctaText: v })}
              placeholder="Help & Support"
            />
            <TextEditor
              label="CTA Button Link"
              value={content.ctaUrl}
              onChange={(v) => handleChange({ ...content, ctaUrl: v })}
              placeholder="#"
            />
          </div>

          {/* Support Popup Settings */}
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Support Popup Settings</h4>
            <TextEditor
              label="Popup Title"
              value={content.supportTitle}
              onChange={(v) => handleChange({ ...content, supportTitle: v })}
              placeholder="Get in Touch"
            />
            <TextEditor
              label="Popup Description"
              value={content.supportDescription}
              onChange={(v) => handleChange({ ...content, supportDescription: v })}
              placeholder="We'd love to hear from you."
              multiline
            />
            <TextEditor
              label="Location Title"
              value={content.supportLocationTitle}
              onChange={(v) => handleChange({ ...content, supportLocationTitle: v })}
              placeholder="Our Current Location"
            />
            <TextEditor
              label="Location Text"
              value={content.supportLocation}
              onChange={(v) => handleChange({ ...content, supportLocation: v })}
              placeholder="4517 Washington Ave. Manchester, Kentucky 39495, USA"
              multiline
            />
            <TextEditor
              label="Google Maps URL"
              value={content.supportLocationMapsUrl}
              onChange={(v) => handleChange({ ...content, supportLocationMapsUrl: v })}
              placeholder="https://maps.google.com/?q=..."
            />
            <TextEditor
              label="Phone Title"
              value={content.supportPhoneTitle}
              onChange={(v) => handleChange({ ...content, supportPhoneTitle: v })}
              placeholder="Phone Number"
            />
            <TextEditor
              label="Phone Text"
              value={content.supportPhone}
              onChange={(v) => handleChange({ ...content, supportPhone: v })}
              placeholder="+(00) 12 - 345 6789\n+(00) 10 - 9876 543"
              multiline
            />
            <TextEditor
              label="Email Title"
              value={content.supportEmailTitle}
              onChange={(v) => handleChange({ ...content, supportEmailTitle: v })}
              placeholder="Email"
            />
            <TextEditor
              label="Email Text"
              value={content.supportEmail}
              onChange={(v) => handleChange({ ...content, supportEmail: v })}
              placeholder="team@example.com\nHoks@example.com"
              multiline
            />
          </div>

          {/* Visibility Controls */}
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Visibility</h4>
            <ToggleEditor
              label="Show Search Bar"
              value={content.showSearch !== false}
              onChange={(v) => handleChange({ ...content, showSearch: v })}
            />
            <ToggleEditor
              label="Show Login Button"
              value={content.showAccount !== false}
              onChange={(v) => handleChange({ ...content, showAccount: v })}
            />
            <ToggleEditor
              label="Show CTA Button"
              value={content.showCta !== false}
              onChange={(v) => handleChange({ ...content, showCta: v })}
            />
          </div>

          {/* Nav Items */}
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Navigation</h4>
            <ListItemEditor
              label="Nav Items"
              items={content.navItems || [
                { label: "Home", sectionId: "hero-default" },
                { label: "About Us", sectionId: "about-default" },
                { label: "HTML", sectionId: "html-default" },
                { label: "Themes", sectionId: "theme-default" },
                { label: "Pages", sectionId: "pages-default" },
                { label: "Blog", sectionId: "blog-default" },
                { label: "Support", sectionId: "support-default" },
              ]}
              itemSchema={[
                { key: "label", label: "Label", type: "text" },
                {
                  key: "sectionId",
                  label: "Section ID",
                  type: "select",
                  options: sectionOptions,
                },
                { key: "url", label: "Custom URL", type: "text" },
              ]}
              onChange={(items) => handleChange({ ...content, navItems: items })}
              addItemText="Add Nav Item"
            />
          </div>
        </>
      }
      styleFields={
        <>
          {/* Padding */}
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Layout</h4>
            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-600">Padding</label>
              <select
                value={content.padding || "normal"}
                onChange={(e) => handleChange({ ...content, padding: e.target.value })}
                className="w-full h-8 px-2 text-xs border border-gray-200 rounded-lg bg-white"
              >
                <option value="compact">Compact</option>
                <option value="normal">Normal</option>
                <option value="spacious">Spacious</option>
              </select>
            </div>
          </div>
        </>
      }
    />
  );
}
